<template>
    <div>
        <div class="table-selection-items">
            <component
                v-if="needFilter && !isClientAccount"
                :is="setFilter"
                :class="$mq == 'sm' ? 'mb-0' : ''"
                :api="api"
                @setFilteringApiParameter="setFilteringApiParameter"
                @universalEmit="universalEmit"
            >
            </component>
            <div 
                class="d-flex ml-auto"
                :class="$mq == 'sm' ? 'mb-0' : 'mb-2'"
            >
                <template v-if="isNeedSearch">
                    <div class="mr-3">
                        <b-icon
                            v-if="$mq != 'sm'"
                            icon="search"
                            scale="1.3"
                            class="table-input-search_icon"
                            variant="secondary"
                        ></b-icon>
                        <input class="form-control table-input-search" type="search" placeholder="Поиск" v-model="filter">
                    </div>
                </template>
                <div class="mr-3" v-if="headerContextMenuName && itemsLocal.headers.length > 0">
                    <b-button
                        size="sm"
                        variant="reload"
                        :id="`context-menu${date}`"
                        class="d-flex align-items-center"
                    >
                        <b-icon icon="gear-wide" scale="1.5" aria-label="Settings"></b-icon>
                    </b-button>
                    <b-popover
                        :target="`context-menu${date}`"
                        triggers="focus hover"
                        :placement="$mq == 'sm' ? 'bottom' : 'left'"
                    >
                        <template #title>
                            <span class="text-uppercase text-secondary">Настройки</span>
                        </template>
                        <tsoft-context-menu-headers
                            :headerContextMenuName="headerContextMenuName"
                            :menuName="'table-context-menu'"
                            :menuItems="itemsLocal.headers"
                        />
                    </b-popover>
                </div>
                <div>
                    <b-button
                        @click="refreshDataInTable()"
                        size="sm"
                        variant="reload"
                    >
                        <b-icon icon="arrow-repeat" scale="1.5" aria-label="Reload" :animation="isTableRefreshing ? 'spin' : ''"></b-icon>
                    </b-button>
                </div>
                <template v-if="$mq != 'sm' && externalExportLink && checkActionAllow('exportPartnerInformation')">
                    <div class="ml-3">
                        <router-link :to="externalExportLink">
                            <b-icon
                                icon="cloud-upload"
                                scale="1.3"
                                class="table-input-search_icon"
                                variant="secondary"
                            ></b-icon>
                        </router-link>
                    </div>
                </template>
                <template v-if="((checkActionAllow('create') && isNeedCreate) || isNeedCreate) && !isClientAccount">
                    <div class="ml-3">
                        <template v-if="$mq != 'sm'">
                            <button
                                @click="createOrEditItemModal()"
                                type="button"
                                class="btn btn-outline-primary font-weight-bold primary-background"
                            >
                                {{ customAddButtonName }}
                            </button>
                        </template>
                        <template v-else>
                            <b-button
                                @click="createOrEditItemModal()"
                                size="sm"
                                variant="reload"
                                id="context-menu"
                            >
                                <b-icon icon="plus" scale="1.5" aria-label="Settings"></b-icon>
                            </b-button>
                        </template>
                    </div>
                </template>
                <b-button 
                    v-if="isExportXLSX && isCanExportXLS" 
                    @click="exportTable(id, exportXLSX)" 
                    size="sm" 
                    variant="primary" 
                    class="d-flex align-items-center ml-3"
                >
                    Экспорт
                    <b-icon class="ml-2 " icon="cloud-download" aria-hidden="true" v-if="!isExportData"></b-icon>
                    <i class="ml-2 fas fa-spinner fa-spin" v-else></i>
                </b-button>
            </div>
        </div>
        <div 
            v-if="needFilter && handlerHeaderTableFilter" 
            class="d-flex justifu-content-start position-relative mb-2" 
            style="height: 17px"
        >
            <template v-for="(value, index) in handlerHeaderTableFilter">
                <template v-if="!Array.isArray(value)">
                    <div 
                        class="select-filter-tag mr-2" 
                        v-if="value" 
                        :key="index" 
                        @click="deleteSelectedFilterValues(index, null)"
                    >
                        <span class="mr-2" v-html="processingSelectedFiltersForOutput(index, value)"></span>
                        <span class="select-filter-clear">&#10006;</span>
                    </div>
                </template>
                <template v-else>
                    <template v-for="(sub_value, sub_index) in value">
                        <div 
                            class="select-filter-tag mr-2" 
                            v-if="sub_value" 
                            :key="`${index}_sub_${sub_index}`" 
                            @click="deleteSelectedFilterValues(index, sub_index)"
                        >
                            <span class="mr-2" v-html="processingSelectedFiltersForOutput(index, sub_value)"></span>
                            <span class="select-filter-clear">&#10006;</span>
                        </div>
                    </template>
                </template>
            </template>
            <div
                class="position-absolute text-right small text-secondary"
                style="right:0; top:0; display: flex; justify-content: space-between; width: 100%;"
            >
                <template v-if="name == 'company' && isSalesManagerAccount">
                    <span class="text-secondary mr-1">
                        <em>В списке отображаются ваши компании и компании, с которыми был контакт, но не случилось поставок.</em>
                    </span>
                </template>
                <template v-else><span> </span></template>
                
                <template v-if="itemsLocal.total && itemsLocal.total > 0">
                    Записей: {{ itemsLocal.total }}{{ itemsLocal.total > 9999 ? "+":"" }}
                </template>
            </div>
        </div>
        <b-table
            ref="table"
            :id="id"
            thead-class="thead-color"
            hover
            show-empty
            :sticky-header="computedWindowHeight"
            sort-icon-left
            :tbody-tr-class="rowClass"
            :stacked="stacked"
            :items="itemsLocal.data"
            :busy.sync="isTableBusy"
            :per-page="tablePerPage"
            :fields="headerContextMenuName ? visibleHeaders : itemsLocal.headers"
            :api-url="api"
            :select-mode="'multi'"
            :selectable="isNeedSelection ? true : false"
            :selected-variant="''"
            @sort-changed="sortingChanged"
            @head-clicked="clearHeaderFilterParameter"
            @row-selected="onRowSelected"
        >
            <template v-slot:head()="scope">
                <div 
                    class="d-flex"
                    :class="(scope.field.headerAlign && scope.field.headerAlign == 'left') ? '':'align-items-center justify-content-center'"
                >
                    <div>
                        {{ scope.label }}
                    </div>

                    <template v-if="typeOfTableFilter == 'order' && needFilter">
                        <template v-if="scope.field.filterKey == 'salesManager' ||
                                        scope.field.filterKey == 'status' ||
                                        scope.field.filterKey == 'outsourcing' ||
                                        scope.field.filterKey == 'orderSource'">
                            <div class="ml-2">
                                <b-button :id="`popover-button-${scope.field.filterKey}`" size="sm" variant="filter-table">
                                    <b-icon icon="filter" scale="1.1"></b-icon>
                                </b-button>
                                <b-popover
                                    :target="`popover-button-${scope.field.filterKey}`"
                                    triggers="focus hover"
                                    placement="bottom"
                                    custom-class="popover-filter-table"
                                >
                                    <template #title>
                                        <span class="text-uppercase text-secondary" v-html="handlerTitleFilterPopover(scope.field.filterKey)"></span>
                                    </template>
                                    <template v-if="scope.field.filterKey == 'status'">
                                        <tsoft-select-of-properties
                                            :reference_id="35"
                                            :needNullElement="false"
                                            :needMultipleSelect="false"
                                            :defaultSelectName="`Выберите статус`"
                                            :internalSearch="false"
                                            :selected="lastSavedHeaderTableFilter.reference_order_status_id"
                                            @set="setOrderStatus"
                                        ></tsoft-select-of-properties>
                                    </template>
                                    <template v-if="scope.field.filterKey == 'outsourcing'">
                                        <multiselect 
                                            v-model="handlerHeaderTableFilter.outsourcing_order" 
                                            :options="['Да', 'Нет']" 
                                            :searchable="false" 
                                            :close-on-select="true" 
                                            :show-labels="false"
                                            placeholder="Выберите статус"
                                        ></multiselect>
                                    </template>
                                    <template v-if="scope.field.filterKey == 'salesManager'">
                                        <tsoft-select-user
                                        id="optional_sales_manager_user_id"
                                        :roles="`sales-manager||sales-head-manager||logistics-manager||logistics-head-manager||logistics-multim-manager||logistics-multim-head-manager||TO-manager||TO-head-manager||president||bsz-manager||b2c-head-manager||b2c-manager`"
                                        :needNullElement="false"
                                        :needMultipleSelect="false"
                                        @set="setSalesManagerValue"
                                        :selected="lastSavedHeaderTableFilter.sales_manager_id"
                                        >
                                        </tsoft-select-user>
                                    </template>
                                    <template v-if="scope.field.filterKey == 'orderSource'">
                                        <tsoft-select-of-properties
                                            :reference_id="4"
                                            :needNullElement="false"
                                            :needMultipleSelect="false"
                                            :defaultSelectName="`Выберите источник`"
                                            :internalSearch="false"
                                            :selected="lastSavedHeaderTableFilter.reference_sources_id"
                                            :excludeArray="[
                                                446, 319, 447, 415, 392, 418, 320, 35, 375, 30, 33, 28, 410, 29,
                                                27, 32, 31, 25, 24, 26, 36, 34
                                            ]"
                                            @set="setOrderSource"
                                        ></tsoft-select-of-properties>
                                    </template>
                                </b-popover>
                            </div>
                        </template>
                    </template>

                    <template v-if="typeOfTableFilter == 'order-service-logistic' && needFilter">
                        <template v-if="scope.field.filterKey == 'logisticsManager' ||
                                        scope.field.filterKey == 'typeOfTeo' ||
                                        scope.field.filterKey == 'typeOfSystemStatus'">
                            <div class="ml-2">
                                <b-button :id="`popover-button-${scope.field.filterKey}`" size="sm" variant="filter-table">
                                    <b-icon icon="filter" scale="1.1"></b-icon>
                                </b-button>
                                <b-popover
                                    :target="`popover-button-${scope.field.filterKey}`"
                                    triggers="focus hover"
                                    placement="bottom"
                                    custom-class="popover-filter-table"
                                >
                                    <template #title>
                                        <span class="text-uppercase text-secondary" v-html="handlerTitleFilterPopover(scope.field.filterKey)"></span>
                                    </template>
                                    <template v-if="scope.field.filterKey == 'logisticsManager'">
                                        <tsoft-select-user
                                            id="optional_teo_manager_user_id"
                                            :roles="`logistics-manager||logistics-head-manager||logistics-multim-manager||logistics-multim-head-manager`"
                                            :needNullElement="false"
                                            :needMultipleSelect="false"
                                            :selected="lastSavedHeaderTableFilter.teo_manager_id"
                                            @set="setTeoManagerValue"
                                        >
                                        </tsoft-select-user>
                                    </template>
                                    <template v-if="scope.field.filterKey == 'typeOfTeo'">
                                        <tsoft-select-of-properties
                                            :reference_id="62"
                                            :needNullElement="false"
                                            :needMultipleSelect="false"
                                            :defaultSelectName="`Выберите тип перевозки`"
                                            :internalSearch="false"
                                            :selected="lastSavedHeaderTableFilter.reference_property_type_id"
                                            @set="setOrderServiceTypeOfTeo"
                                        ></tsoft-select-of-properties>
                                    </template>
                                    <template v-if="scope.field.filterKey == 'typeOfSystemStatus'">
                                        <tsoft-select-of-properties
                                            :reference_id="28"
                                            :needNullElement="false"
                                            :needMultipleSelect="false"
                                            :defaultSelectName="`Выберите источник`"
                                            :internalSearch="false"
                                            :selected="lastSavedHeaderTableFilter.reference_property_status_id"
                                            @set="setOrderServiceStatus"
                                        ></tsoft-select-of-properties>
                                    </template>
                                </b-popover>
                            </div>
                        </template>
                    </template>                    
                </div>
            </template>
            <template #cell(selected)="{ rowSelected, data }">
                <div class="text-center">
                    <template v-if="rowSelected">
                      <span class="font-size-12 font-weight-bold">&check;</span>
                    </template>
                    <template v-else>
                      <span class="font-size-12 text-muted-deep">&check;</span>
                    </template>
                </div>
            </template>
            <template #cell(index)="data">
                <div class="text-center">{{ data.index + 1 }}</div>
            </template>
            <template #cell(id)="data">
                <template v-if="name === 'order' && !isClientAccount && !isLeadObserverAccount">
                    <span
                        class="sidebar-link text-primary font-weight-bold"
                        @click="openOrderSidebar(data.item.id, '', '')"
                        title="Открыть заказ"
                    >
                        #{{ data.item.id }}
                    </span>
                </template>
                <template v-else>
                    #{{ data.item.id }}
                </template>
                <template v-if="data.item.text_mark">
                    <div class="text-muted font-size-12 text-center">{{ data.item.text_mark }}</div>
                </template>
            </template>
            <template v-slot:cell(selected_services)="data">
                <div v-if="data.item.selected_services && data.item.selected_services.length > 0">
                    <b-badge
                        v-for="(item, index) in data.item.selected_services"
                        :key="item.id"
                        variant="success"
                        class="mr-1 mb-1 cursor-pointer special-td-badge"
                        @click="openOrderSidebar(data.item.id, '', item.id)"
                        :title="`Открыть детали услуги: ${item.name}`"
                    >
                        {{ item.name }}
                    </b-badge>
                </div>
                <div class="text-center" v-else>—</div>
            </template>
            <template v-slot:cell(order_id)="data">
                <span v-if="!data.item.order_id">—</span>
                <span
                    v-else
                    class="font-weight-bold sidebar-link text-primary"
                    @click="openOrderSidebar(data.item.order_id, '', '')"
                    title="Открыть заказ"
                >
                    #{{ data.item.order_id }}
                </span>
            </template>
            <template #cell(order.ordering_number)="data">
                <span
                    v-if="data.item.order_id"
                    class="font-weight-bold sidebar-link text-primary"
                    @click="openOrderSidebar(data.item.order_id, '', '')"
                    title="Открыть заказ"
                >
                    #{{ data.item.order_id }}
                </span>
                <span v-else>
                    —
                </span>
            </template>
            <template #cell(operation_type_label)="data">
                <template v-if="data.item.operation_type == 'debit'">
                    <b-badge 
                        variant="success"
                        class="special-td-badge"
                    >
                        {{ data.value }}
                    </b-badge>
                </template>
                <template v-else>
                    <b-badge 
                        variant="danger"
                        class="special-td-badge"
                    >
                        {{ data.value }}
                    </b-badge>
                </template>
            </template>

            <template #cell(status_label)="data">
                <b-badge 
                    variant="secondary" 
                    class="special-td-badge"
                >
                    {{ data.value }}
                </b-badge>
            </template>

            <template #cell(amount)="data">
                <span class="font-weight-bold">{{ formatNumbers(data.value) }} руб.</span>
            </template>
            <template #cell(margin_base)="data">
                <span class="font-weight-bold">{{ formatNumbers(data.value) }} руб.</span>
            </template>
            <template #cell(commission_rate)="data">
                <span class="font-weight-bold">{{ formatNumbers(data.value) }} %</span>
            </template>
            <template #cell(description_html)="data">
                <span v-html="data.value"></span>
            </template>
            <template #cell(operation_generation)="data">
                <span v-html="data.value"></span>
            </template>
            <template #cell(comment_latest.comment)="data">
                <span v-html="data.value"></span>
            </template>
            <template #cell(last_comment.comment)="data">
                <template v-if="data.item.last_comment">
                    <div class="company-last-comment"><span v-html="data.value"></span></div>
                    <div class="text-right h5-small-text text-secondary">{{ data.item.last_comment.created_at }}</div>
                </template>
                <template v-else>
                    —
                </template>
            </template>

            <template #cell(agent_company_name)="data">
                <template v-if="data.item.agent_company">
                    <span
                        class="font-weight-bold"
                    >
                        {{ data.item.agent_company.full_name }}
                    </span>
                </template>
                <template v-else>
                    <span class="font-weight-bold">—</span>
                </template>
            </template>

            <template #cell(company.name)="data">
                <template v-if="data.item.company_id && !isClientAccount && !isLeadObserverAccount">
                    <span
                        class="sidebar-link font-weight-bold"
                        @click="openCompanySidebar(data.item.company_id)"
                        title="Открыть компанию"
                    >
                        {{ companyNameTdOutput(data.item, data.value) }}
                    </span>
                </template>
                <template v-else>
                    {{ companyNameTdOutput(data.item, data.value) }}
                </template>
            </template>

            <template #cell(only_teo_users.name)="data">
                {{ userNamesTdOutput(data.item, data.item.only_teo_users) }}
            </template>

            <template #cell(external_company.name)="data">
                <template v-if="data.item.external_company_id && !isClientAccount && !isLeadObserverAccount">
                    <span
                        class="sidebar-link font-weight-bold"
                        @click="openCompanySidebar(data.item.external_company_id)"
                        title="Открыть компанию"
                    >
                        {{ companyNameTdOutput(data.item, data.value) }}
                    </span>
                </template>
                <template v-else>
                    {{ companyNameTdOutput(data.item, data.value) }}
                </template>
            </template>
            <template #cell(name)="data">
                <template v-if="data.item.company_id && !isClientAccount && !isLeadObserverAccount">
                    <span
                        class="sidebar-link font-weight-bold"
                        @click="openCompanySidebar(data.item.id)"
                        title="Открыть компанию"
                    >
                        {{ companyNameTdOutput(data.item, data.value) }}
                    </span>
                </template>
                <template v-else>
                    {{ companyNameTdOutput(data.item, data.value) }}
                </template>
            </template>
            <template #cell(for_company.name)="data">
                <template v-if="data.item.company_id && !isClientAccount && !isLeadObserverAccount">
                    <span
                        class="sidebar-link font-weight-bold"
                        @click="openCompanySidebar(data.item.company_id)"
                        title="Открыть компанию"
                    >
                        {{ companyNameTdOutput(data.item, data.value) }}
                    </span>
                </template>
                <template v-else>
                    {{ companyNameTdOutput(data.item, data.value) }}
                </template>
            </template>

            <template #cell(invoice_number)="data">
                <span class="font-weight-bold text-primary" v-if="data.value">{{ data.value }}</span>
                <span class="font-weight-bold text-secondary" v-else>—</span>
            </template>
            <template #cell(service.reference.name)="data">
                <b-badge 
                    variant="secondary" 
                    class="special-td-badge"
                >
                    {{ data.value }}
                </b-badge>
            </template>

            <template #cell(sum)="data">
                <span class="font-weight-bold">{{ formatNumbers(data.value) }}</span>
            </template>
            <template #cell(sum_currency)="data">
                <span class="font-weight-bold">{{ formatNumbers(data.value) }}</span>
            </template>
            <template #cell(debt)="data">
                <span class="font-weight-bold" :class="data.value > 0 ? 'text-danger' : 'text-success'">{{ formatNumbers(data.value) }}</span>
            </template>
            <template #cell(sum_payment)="data">
                <span class="font-weight-bold">{{ formatNumbers(data.value) }}</span>
            </template>
            <template #cell(sum_difference)="data">
                <span class="font-weight-bold">{{ formatNumbers(data.value) }}</span>
            </template>
            <template #cell(commission_bank)="data">
                <span class="font-weight-bold">{{ formatNumbers(data.value) }}</span>
            </template>
            <template #cell(companies)="data">
                {{ companiesNamesTdOutput(data.item, data.value) }}
            </template>
            <template #cell(lead_segments)="data">
                <div class="small-td-table"><span class="no-wrap" v-html="byNameObjectGlueOutput(data.item.segments, '<br>')"></span></div>
            </template>
            <template #cell(set_services_ids)="data">
                <div class="small-td-table"><span class="no-wrap" v-html="referencePropertyArrayGlueOutput(data.item.set_services_ids, '<br>')"></span></div>
            </template>
            <template #cell(set_special_features_ids)="data">
                <div class="small-td-table"><span class="no-wrap" v-html="referencePropertyArrayGlueOutput(data.item.set_special_features_ids, '<br>')"></span></div>
            </template>
            <template #cell(set_transit_directions_ids)="data">
                <div class="small-td-table"><span class="no-wrap" v-html="referencePropertyArrayGlueOutput(data.item.set_transit_directions_ids, '<br>')"></span></div>
            </template>
            <template #cell(set_rf_directions_ids)="data">
                <div class="small-td-table"><span class="no-wrap" v-html="referencePropertyArrayGlueOutput(data.item.set_rf_directions_ids, '<br>')"></span></div>
            </template>
            <template #cell(set_countries_of_branches_ids)="data">
                <div class="small-td-table"><span v-html="referencePropertyArrayGlueOutput(data.item.set_countries_of_branches_ids, ', ')"></span></div>
            </template>
            <template #cell(set_countries_of_rolling_stock_ids)="data">
                <div class="small-td-table"><span v-html="referencePropertyArrayGlueOutput(data.item.set_countries_of_rolling_stock_ids, ', ')"></span></div>
            </template>
            <template #cell(type_of_company_ts)="data">
                <div class="small-td-table"><span class="no-wrap" v-html="arrayGlueOutput(data.item.type_of_company_ts, '<br>')"></span></div>
            </template>
            <template #cell(kind_of_company_ts)="data">
                <div class="small-td-table"><span class="no-wrap" v-html="arrayGlueOutput(data.item.kind_of_company_ts, '<br>')"></span></div>
            </template>
            <template #cell(set_uploading_geography_ids)="data">
                <div class="small-td-table"><span v-html="referencePropertyArrayGlueOutput(data.item.set_uploading_geography_ids, ', ')"></span></div>
            </template>
            <template #cell(set_unloading_geography_ids)="data">
                <div class="small-td-table"><span v-html="referencePropertyArrayGlueOutput(data.item.set_unloading_geography_ids, ', ')"></span></div>
            </template>
            <template #cell(reference_status_id)="data">
                <b-badge class="special-td-badge">{{ referencePropertyById(data.item.reference_status_id) }}</b-badge>
            </template>
            <template #cell(set_transport_data)="data">
                <div 
                    v-for="(category, category_name) in transportArrayGroupedByType(data.item.set_transport_data)"
                    :key="category_name"
                    class="small-td-table"
                >
                    <b>{{ category_name }}</b>: <span v-html="transportArrayGlueOutput(category)"></span>
                </div>
            </template>
            <template #cell(set_insurance_data)="data">
                <div class="small-td-table"><span v-html="referencePropertyArrayGlueOutput(data.item.set_insurance_data, '<br>')"></span></div>
            </template>
            <template #cell(with_nds_text)="data">
                <template v-if="data.item.size_nds && data.item.size_nds > 0">{{ data.item.size_nds }}%</template>
                <template v-else>{{ data.item.with_nds_text }}</template>
            </template>

            <template #cell(desire)="data">
                <template v-if="data.item.selected">
                    <span class="text-success font-bold">Выбрали к сотрудничеству</span>
                </template>
                <template v-else>
                    <template v-if="data.item.desire === true">
                        <span class="text-success font-bold">Можно сотрудничать</span>
                    </template>
                    <template v-else-if="data.item.desire === false">
                        <span class="text-danger font-bold">Не будем сотрудничать</span>
                    </template>
                    <template v-else>
                        <span class="text-muted font-bold">Не определились</span>
                    </template>
                </template>
            </template>

            <template #cell(company_employee.lead_fio)="data">
                <div class="small-td-table" v-if="data.item.company_employee && data.item.company_employee.lead_fio">
                    <b class="name">{{ data.item.company_employee.lead_fio }}</b> <i class="fas fa-copy copy-icon" @click="copyToClipboardInTable(data.item.company_employee.lead_fio)"></i><br>
                    <span v-if="data.item.company_employee.lead_phone">
                        <a :href="`tel:+${data.item.company_employee.lead_phone}`">+{{ data.item.company_employee.lead_phone }}</a>
                        <i class="fas fa-copy copy-icon" @click.stop="copyToClipboardInTable(data.item.company_employee.lead_phone)"></i>
                        <br>
                    </span>
                    <span v-if="data.item.company_employee.lead_company_phone">
                        {{ data.item.company_employee.lead_company_phone }}
                        <i class="fas fa-copy copy-icon" @click.stop="copyToClipboardInTable(data.item.company_employee.lead_company_phone)"></i>
                        <br>
                    </span>
                    <span v-if="data.item.company_employee.lead_email">
                        <a :href="`mailto:${data.item.company_employee.lead_email}`">{{ data.item.company_employee.lead_email }}</a>
                        <i class="fas fa-copy copy-icon" @click.stop="copyToClipboardInTable(data.item.company_employee.lead_email)"></i>
                        <br>
                    </span>
                    <span v-if="data.item.company_employee.lead_segments">{{ data.item.company_employee.lead_segments }}</span>
                </div>
                <div v-else class="text-center">—</div>
            </template>
            <template #cell(company_employee_contacts)="data">
                <template v-if="!data.item.company_employees || data.item.company_employees.length == 0">—</template>
                <template v-else>
                  <div v-for="contact in prepareEmployees(data.item.company_employees)" class="small-td-table">
                    <b class="name">{{ contact.lead_fio }}</b> <i class="fas fa-copy copy-icon" @click="copyToClipboardInTable(contact.lead_fio)"></i><br>
                    <span v-if="contact.lead_phone">
                      <a :href="`tel:+${contact.lead_phone}`">+{{ contact.lead_phone }}</a>
                      <i class="fas fa-copy copy-icon" @click.stop="copyToClipboardInTable(contact.lead_phone)"></i>
                      <br>
                    </span>
                    <span v-if="contact.lead_company_phone">
                      {{ contact.lead_company_phone }}
                      <i class="fas fa-copy copy-icon" @click.stop="copyToClipboardInTable(contact.lead_company_phone)"></i>
                      <br>
                    </span>
                    <span v-if="contact.lead_email">
                      <a :href="`mailto:${contact.lead_email}`">{{ contact.lead_email }}</a>
                      <i class="fas fa-copy copy-icon" @click.stop="copyToClipboardInTable(contact.lead_email)"></i>
                      <br>
                    </span>
                    <span v-if="contact.lead_segments">{{ contact.lead_segments }}</span>
                  </div>
                </template>
            </template>
            <template #cell(is_transport_company)="data">
                {{ data.item.is_transport_company == 1 ? 'Да':'Нет' }}
            </template>
            <template #cell(is_available)="data">
                {{ data.item.is_available == 1 ? 'Да':'Нет' }}
            </template>
            <template #cell(latest_dadata_check)="data">
                <template v-if="data.item.latest_dadata_check">
                    <b-badge 
                        :variant="data.item.latest_dadata_check.is_liquidated ? 'danger' : 'success'" 
                        class="px-2 py-2 special-td-badge"
                        title="Статус выставлен в ходе последней автоматической проверки из открытых источников"
                    >
                        {{ data.item.latest_dadata_check.is_liquidated ? 'Ликвидирована' : 'Активна' }}
                    </b-badge>

                </template>
                <template v-else>
                    <b-badge 
                        variant="secondary" 
                        class="px-2 py-2 special-td-badge"
                        title="Статус выставлен в ходе последней автоматической проверки из открытых источников"
                    >
                        Не проверялась
                    </b-badge>
                </template>
            </template>
            <template #cell(policy_scan)="data">
                <template v-if="data.item.scan">
                    <a :href="hfs+data.item.scan.path" target="_blank">Скачать &raquo;</a>
                </template>
                <template v-else>
                    Скан не приложен
                </template>
            </template>

            <template #cell(services_list)="data">
                <div class="big-table-cells">
                    <template v-if="data.item.services_list">
                        <span v-html="formatServicesList(data.item.services_list)"></span>
                    </template>
                    <template v-else>
                        —
                    </template>
                </div>
            </template> 
            
            <template #cell(latest_status_with_comments)="data">
                <div class="big-table-cells">
                    <template v-if="data.item.latest_status_with_comments">
                        <span v-html="getLatestComment(data.item.latest_status_with_comments)"></span>
                    </template>
                    <template v-else>
                        —
                    </template>
                </div>
            </template>

            <template #cell(title_and_description)="data">
                    <template v-if="data.item.title">
                        <span v-html="data.item.title" class="font-weight-bold"></span>
                    </template>
                    <template v-if="data.item.description">
                        <div class="big-table-cells mt-1">
                            <span v-html="data.item.description" class="font-italic"></span>
                        </div>
                    </template>
            </template>

            <template #cell(company_name)="data">
                <template v-if="data.item.company_name">
                    <span class="font-weight-bold">{{ data.item.company_name }}</span>
                </template>
                <template v-else>
                    <span class="font-weight-bold">—</span>
                </template>
            </template>

            <template #cell(status.name)="data">
                <template v-if="isClientAccount && isClientQuotationPending(data.item)">
                    <span>Формируем предложение и готовим коммерческое предложение</span>
                </template>
                <template v-else-if="data.item.status">
                    <b-badge class="special-td-badge">{{ data.item.status.name }}</b-badge>
                </template>
                <template v-else>
                    —
                </template>
            </template>
            
            <template #cell(cooperation_status_label)="data">
                <template v-if="data.item.cooperation_status_label">
                    <b-badge class="special-td-badge" :variant="data.item.cooperation_status_color">{{ data.item.cooperation_status_label }}</b-badge>
                </template>
                <template v-else>
                    —
                </template>
            </template>

            <template #cell(phones)="data">
                <template v-if="data.item.phones">
                    <span v-for="phone in data.item.phones" :key="phone" class="font-weight-bold">{{ phone }}</span>
                </template>
                <template v-else>
                    —
                </template>
            </template>

            <template #cell(phones_cold_clients)="data">
                <template v-if="data.item.phones">
                    <div v-for="phone in data.item.phones" :key="phone" class="font-weight-bold">
                        <a :href="`tel:${phone}`">{{ phone }}</a>
                    </div>
                </template>
                <template v-else>
                    —
                </template>
            </template>

            <template #cell(email)="data">
                <template v-if="data.item.email">
                    <a :href="`mailto:${data.item.email}`">{{ data.item.email }}</a>
                </template>
                <template v-else>
                    —
                </template>
            </template>

            <template #cell(website)="data">
                <template v-if="data.item.website">
                    <a :href="data.item.website" target="_blank">{{ data.item.website }}</a>
                </template>
                <template v-else>
                    —
                </template>
            </template>

            <template #cell(comment)="data">
                <div class="big-table-cells">
                    <template v-if="data.item.comment">
                        <span v-html="data.item.comment"></span>
                    </template>
                    <template v-else>
                        —
                    </template>
                </div>
            </template>  
             
            <template #cell(notes)="data">
                <div class="big-table-cells">
                    <template v-if="data.item.notes">
                        <span v-html="data.item.notes"></span>
                    </template>
                    <template v-else>
                        —
                    </template>
                </div>
            </template>            
            
            <template #cell(email_send_status)="data">
                <template v-if="data.item.email_send_status && data.item.email_send_status.code === 'sent'">
                    <span class="text-success font-weight-bold">Отправлено</span>
                </template>
                <template v-else-if="data.item.email_send_status && data.item.email_send_status.code === 'retrying'">
                    <span
                        class="text-warning font-weight-bold"
                        v-b-tooltip.hover
                        :title="data.item.email_send_status.error_message || ''"
                    >{{ data.item.email_send_status.label }}</span>
                </template>
                <template v-else-if="data.item.email_send_status && data.item.email_send_status.code === 'failed'">
                    <span
                        class="text-danger font-weight-bold"
                        v-b-tooltip.hover
                        :title="data.item.email_send_status.error_message || ''"
                    >Не отправлено</span>
                </template>
                <template v-else>
                    <span class="text-muted">В очереди</span>
                </template>
            </template>
            <template #cell(error_message)="data">
                <template v-if="data.item.error_message">
                    <b-icon 
                        icon="exclamation-triangle" 
                        variant="danger"
                        v-b-tooltip.hover :title="data.item.error_message"
                    ></b-icon>

                    <div title="После достижения 5 попыток, письмо отправлено не будет">Попыток отправки: {{ data.item.retry_attempts }}/5</div>
                </template>
                <template v-else>
                    {{ '—' }}
                </template>
            </template>
            <template #cell(order.company.name)="data">
                <template v-if="data.item.invoice_type == 'outgoing'">
                    <template v-if="data.item.payer_company_id && data.item.payer_company_id != 1">
                        <template v-if="!isClientAccount && !isLeadObserverAccount">
                            <span
                                class="sidebar-link"
                                @click="openCompanySidebar(data.item.payer_company_id)"
                                title="Открыть компанию"
                            >
                                {{ data.item.payer_company.name }}
                            </span>
                        </template>
                        <template v-else>{{ data.item.payer_company.name }}</template>
                    </template>
                    <template v-else>
                        <template v-if="!isClientAccount && !isLeadObserverAccount">
                            <span
                                class="sidebar-link"
                                @click="openCompanySidebar(1)"
                                title="Открыть компанию"
                            >
                                АО "ТАНАИС"
                            </span>
                        </template>
                        <template v-else>АО "ТАНАИС"</template>
                    </template>
                </template>
                <template v-else>
                    <!-- @TODO: Накрутка логики, надо попробовать исправить -->
                    <template v-if="!isClientAccount && !isLeadObserverAccount">
                        <span
                            class="sidebar-link"
                            title="Открыть компанию"
                            @click="openCompanySidebar(data.item.order ? data.item.order.company_id : data.item.payer_company_id)"
                        >
                            {{ companyNameTdOutput(data.item, data.value) }}
                        </span>
                    </template>
                    <template v-else>
                        {{ companyNameTdOutput(data.item, data.value) }}
                    </template>
                </template>
            </template>
            <template #cell(company.to_manager.name)="data">
                {{ findUserByServiceType(22, 'to_manager', data) }}
            </template>
            <template #cell(company.teo_manager.name)="data">
                {{ findUserByServiceType(23, 'teo_manager', data) }}
            </template>
            <template #cell(turning)="data">
                {{ data.item.turning != null ? data.item.turning : '—' }}
            </template>
            <template #cell(bitrix_user_id)="data">
                {{ data.item.bitrix_user_id != null ? data.item.bitrix_user_id : '—' }}
            </template>
            
            <template #cell(order_score.score)="data">
                <template v-if="data.value">
                    <span style="font-size: 18px;">{{ data.value }}</span><span style="font-size: 12px;">/10</span>
                </template>

                <template v-else>
                    <template v-if="data.field.key === 'order_score.score' && items.actionAllows.includes('can-score-order')">
                        <b-button
                            size="sm"
                            class="btn btn-secondary"
                            title="Оценить поставку"
                            @click="openScoreSidebar(data)"
                        >Оценить поставку</b-button>
                    </template>
                    <template v-else> - </template>
                </template>
            </template>
            <template #cell(score)="data">
                <template v-if="data.value >= 0">
                    <span style="font-size: 18px;">{{ data.value }}</span><span style="font-size: 12px;">/10</span>
                </template>
                <template v-else>
                    {{ '—' }}
                </template>
            </template>
            <template v-slot:cell(actions)="row">
                <div class="ml-auto mr-auto text-center table-action-cell" :style="actionStickyMinWidth()">
                    <component
                        v-if="!isClientAccount && !isLeadObserverAccount"
                        :is="`action-${name}`"
                        :row.sync="row"
                        :actionAllows="items.actionAllows"
                        @universalEmit="universalEmit"
                    ></component>
                    <component
                        v-else
                        :is="`action-${nameClient}-client`"
                        :row.sync="row"
                        :actionAllows="items.actionAllows"
                        @universalEmit="universalEmit"
                    ></component>
                </div>
            </template>
            <template #cell()="data">
                <template v-if="data.field.loading">
                    <template v-if="!data.value"><i class="fas fa-spinner fa-spin ml-1"></i></template>
                    <template v-else>{{ data.value }}</template>
                </template>
                <template v-else>
                    {{ data.value || "—" }}
                </template>
            </template>
            <template #cell(draft)="data">
                {{ data.item.draft == 1 ? 'Да' : 'Нет' }}
            </template>
            <template #cell(is_liquidated)="data">
                {{ data.item.is_liquidated == 1 ? 'Да' : 'Нет' }}
            </template>
            <template v-slot:table-busy>
                <div v-for="skeletonItem in 12" :key="skeletonItem">
                    <b-skeleton type="input" height="40px" class="mb-3"></b-skeleton>
                </div>
            </template>
            <template #empty="scope">
                <p class="my-4"><em>{{ onDataEmptyMessage }}</em></p>
            </template>
            <template #emptyfiltered="scope">
                <p class="my-4"><em>По заданному фильтру ничего не найдено</em></p>
            </template>
        </b-table>
        <div class="table-pagination-block mt-2">
            <div
              class="align-items-center position-absolute d-flex"
              style="left: 0; top: 0;"
              v-if="isNeedSelection && !$route.params.service_id"
            >
              <div>
                <b-form-checkbox
                  class="d-inline"
                  id="checkbox-all-rows"
                  v-model="selectAllRows"
                  name="checkbox-all-rows"
                  :value="true"
                  :unchecked-value="false"
                  @change="clickOnSelectAllRows"
                  title="Выбрать все видимые элементы"
                >
                  Выбрано: {{ selected.length }}
                </b-form-checkbox>
              </div>
              <div
                v-if="selected.length > 0"
              >
                <component
                  :is="`footer-action-${name}`"
                  :actionAllows="items.actionAllows"
                  :selected="selected"
                  @universalEmit="universalEmit"
                ></component>
              </div>
            </div>
            <div>
                <div>
                    <b-pagination
                        v-show="itemsLocal.total > 5"
                        v-model="itemsLocal.current_page"
                        :total-rows="itemsLocal.total"
                        :per-page="tablePerPage"
                        :current-page="itemsLocal.current_page"
                        aria-controls="main-table"
                        size="md"
                        class="my-0"
                        pills
                    ></b-pagination>
                </div>
            </div>
            <div class="align-items-center d-flex position-absolute table-pagination__perpage">
                <div v-if="$mq != 'sm'" class="font-size-12 text-secondary mr-1"  v-show="itemsLocal.total > 5">
                    <span>Показывать по</span>
                </div>
                <div v-show="itemsLocal.total > 5">
                    <b-form-select
                        v-model="tablePerPage"
                        id="perPageSelect"
                        class="table-select-perpage"
                        size="sm"
                        :options="pageOptions"
                    ></b-form-select>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import eventBus from '@/eventBuses/eventBus.js';

import debounce from '@/services/helpers';
import { API_POST_SERVICE_PROPERTIES_BY_ORDER_LIST, HOST_FOR_STORAGE } from '@/constants/api';

import { checkActionAllow, excelExportHelpers, tablePrepareData } from '@/mixins';
import { toCurrency, formattedDate, formattedDateTime, phoneMask } from '@/mixins/textFilters';
import { openCompanySidebar } from '@/mixins/openCompanySidebar';
import { openOrderSidebar } from '@/mixins/openOrderSidebar';

import OrderTableFilter from '@/components/orders/OrderTableFilter';
import OrderServiceTableFilter from '@/components/orders/OrderServiceTableFilter';
import OrderServiceLogisticTableFilter from '@/components/orders/OrderServiceLogisticTableFilter';
import FinanceTableFilter from '@/components/finance/FinanceTableFilter';
import FinanceRequestsTableFilter from '@/components/finance/FinanceRequestsTableFilter';
import CompanyTableFilter from '@/components/companies/CompanyTableFilter';
import B2cCompanyTableFilter from '@/components/companies/B2cCompanyTableFilter';
import AgentCompaniesTableFilter from '@/components/agentCompanies/AgentCompanyTableFilter';
import AgentSettlementsTableFilter from '@/components/agentSettlements/AgentSettlementTableFilter';
import NewsTableFilter from '@/components/news/NewsTableFilter';
import ColdClientTableFilter from '@/components/coldClients/ColdClientTableFilter';

import ActionOrder from "./actions/actionOrder";
import ActionFinance from "./actions/actionFinance";
import ActionFinanceClient from "./actions/actionFinanceClient";
import ActionFinanceRequest from "./actions/actionFinanceRequest";
import ActionFinanceVirtual from "./actions/actionFinanceVirtual";
import ActionFinanceSplit from "./actions/actionFinanceSplit";
import ActionAgentCompany from "./actions/actionAgentCompany";
import ActionCustom from "./actions/actionCustom";
import ActionCompany from "./actions/actionCustom";
import ActionOrderLogistic from "./actions/actionOrderLogistic";
import ActionCustomClient from "./actions/actionCustomClient";
import ActionEmailTracking from "./actions/actionEmailTracking";
import ActionOrderSuppliersRequest from "./actions/actionOrderSuppliersRequest";
import ActionAgentSettlement from "./actions/actionAgentSettlement";
import ActionNews from "./actions/actionNews";
import ActionColdClient from "./actions/actionColdClient";
import ActionAgentWithdrawalRequest from "./actions/actionAgentWithdrawalRequest";

import FooterActionOrder from "./actionsFooter/actionOrder"
import FooterActionCompany from "./actionsFooter/actionCompany"
import FooterActionFinance from "./actionsFooter/actionFinance"

const initalHeaderTableFilter = () => ({
    // Orders
    reference_order_status_id : [],
    sales_manager_id : [],
    outsourcing_order: '',
    reference_sources_id: [],

    // Order service
    teo_manager_id: [],
    reference_property_type_id: [],
    reference_property_status_id: []
})

export default {
    props: {
        id: { type: [String, Number] },
        name: { type: String, default: 'custom' },             // Naming of table for specify moments
        nameClient: { type: String, default: 'custom' },             // Naming of table for specify moments for client
        api: { type: String, required: true },                                  // Link on API for table working
        items: { type: Object, required: true },                                // Main items Object
        exportXLSX: { type: String, required: false },                          // If need export xlsx
        isNeedSearch: { type: Boolean, default: true, required: true },                        // If table needs search line
        isNeedCreate: { type: Boolean, required: false, default: true },        // If table needs create new element button
        isNeedSelection: { type: Boolean, required: false, default: false },    // If table needs selection of elements
        externalExportLink: { type: String, required: false, default: '' },     // If table needs external export link on page for example
        typeOfTableFilter: { type: String, required: false, default: '' },      // Type of filter for output: orders, services, finance
        additionalGetParameter: { type: String, required: false, default: '' }, // If we want to add something, for example, &order_id=10
        customAddButtonName: { type: String, required: false, default: 'Создать' },      // If we need to rename add button
        onDataEmptyMessage: {
            type: String,
            required: false,
            default: 'Таблица временно пуста. Попробуйте добавить новую запись.'
        },                                                                       // Custom Message on empty data in table
        headerContextMenuName: { type: String, required: false, default: '' },   // If table header needs context menu column filter - type filter name
        needFilter: { type: Boolean, default: true },
        specificApiRequest: {type: Object}
    },
    components: {
        OrderTableFilter,
        OrderServiceTableFilter,
        OrderServiceLogisticTableFilter,
        FinanceTableFilter,
        FinanceRequestsTableFilter,
        CompanyTableFilter,
        B2cCompanyTableFilter,
        AgentCompaniesTableFilter,
        AgentSettlementsTableFilter,
        NewsTableFilter,
        ColdClientTableFilter,
        ActionOrder,
        ActionFinance,
        ActionFinanceClient,
        ActionFinanceRequest,
        ActionFinanceVirtual,
        ActionFinanceSplit,
        ActionAgentCompany,
        ActionCustom,
        ActionCompany,
        ActionOrderLogistic,
        ActionCustomClient,
        ActionEmailTracking,
        ActionOrderSuppliersRequest,
        ActionAgentSettlement,
        ActionNews,
        ActionColdClient,
        ActionAgentWithdrawalRequest,
        FooterActionOrder,
        FooterActionCompany,
        FooterActionFinance,
    },
    mixins: [
        checkActionAllow,
        toCurrency,
        formattedDate,
        formattedDateTime,
        phoneMask,
        excelExportHelpers,
        openCompanySidebar,
        openOrderSidebar,
        tablePrepareData
    ],
    data() {
        return {
            handlerHeaderTableFilter: {
                // Orders
                reference_order_status_id : [],
                sales_manager_id : [],
                outsourcing_order: '',
                reference_sources_id: [],

                // Order service
                teo_manager_id: [],
                reference_property_type_id: [],
                reference_property_status_id: []
            },
            lastSavedHeaderTableFilter: {
                // Orders
                reference_order_status_id : '',
                sales_manager_id : '',
                outsourcing_order: '',
                reference_sources_id: '',

                // Order service
                teo_manager_id: '',
                reference_property_type_id: '',
                reference_property_status_id: ''
            },            
            computedWindowHeight: '',
            selectedFilterValues: {},

            pageOptions: [5, 10, 25, 50, 100],
            isTableBusy: true,
            isTableRefreshing: true,
            filter: '',                                      // Search line value
            additionalParameter: '',
            additionalHeaderTableParameter: '',
            sortParamsLine: '',
            visibleHeaders: [],
            selected: [],
            selectAllRows: false,
            date: new Date(),
            hfs: HOST_FOR_STORAGE,
        }
    },
    created() {
        window.addEventListener('resize', this.handlerHeightWindow);
        eventBus.$on('reload-main-table', this.refreshDataInTable);
    },
    destroyed() {
        window.removeEventListener('resize', this.handlerHeightWindow);
        eventBus.$off('reload-main-table', this.refreshDataInTable);
    },
    computed: {
        ...mapGetters('tablesSettings', ['tableSettings']),
        ...mapGetters("auth", ["currentUser"]),

        isCanExportXLS() {
            return (this.currentUser.role.includes("administrator") ||
                this.currentUser.role.includes("client") ||
                this.currentUser.id === 11 || // Ольга Зуева
                this.currentUser.id === 12 || // Игорь Шустов
                this.currentUser.id === 17 || // Елена Кретова 
                this.currentUser.id === 18    // Рената Курбаева            
            );
        },

        isB2cDepartment() {
            return (
                this.currentUser.role.includes("b2c-head-manager") ||
                this.currentUser.role.includes("b2c-manager")
            );
        },

        setFilter() {
            if (this.typeOfTableFilter) {
                if (this.typeOfTableFilter == 'company') {
                    if (this.isB2cDepartment) {
                        return `b2c-${this.typeOfTableFilter}-table-filter`
                    }

                    return `${this.typeOfTableFilter}-table-filter`
                } 

                if (this.typeOfTableFilter == 'cold-client') {
                    return 'ColdClientTableFilter'
                }

                // Для order-service возвращаем правильное имя компонента
                if (this.typeOfTableFilter == 'order-service') {
                    return 'OrderServiceTableFilter'
                }

                return `${this.typeOfTableFilter}-table-filter`                
            }
        },
        itemsLocal: {
            get: function() {
                if (this.tableHeaderSettings[this.headerContextMenuName]) {
                    let savedHeaderFilter = this.tableHeaderSettings[this.headerContextMenuName];
    
                    this.items.headers.forEach(headerItem => {
                        if (savedHeaderFilter[headerItem.key]) {
                            headerItem.visible = savedHeaderFilter[headerItem.key].visible
                        }
                    })
                }
                return this.items
            },
            set: function(value) {
                this.$emit('update:items', value)
            }
        },
        stacked() {
            const stackedBreakpoints = ['sm'];
            return stackedBreakpoints.includes(this.$mq);
        },
        responsive() {
            const responsiveBreakpoints = ['md', 'lg', 'xl'];
            return responsiveBreakpoints.includes(this.$mq);
        },
        tableHeaderSettings() {
            return this.$store.getters['userLocalSettings/TABLE_HEADER_SETTINGS']
        },
        isExportXLSX() {
            return this.id && this.exportXLSX && this.items.data.length > 0
        },
        tablePerPage: {
            get: function() {
                return this.tableSettings.perPage
            },
            set: function(value) {
                this.setTableSettings({name: 'perPage', value: value})
            }
        },
        isClientAccount() {
            return this.currentUser.role.includes('client');
        },
        isLeadObserverAccount() {
            return this.currentUser.role.includes('lead-observer');
        },
        isSalesManagerAccount() {
            return this.currentUser.role.includes('sales-manager');
        },
    },
    watch: {
        'handlerHeaderTableFilter.outsourcing_order': {
            handler() {
                this.changeActiveFilter(this.handlerHeaderTableFilter);
            },
            deep: false
        },
        'handlerHeaderTableFilter.sales_manager_id': {
            handler() {
                this.changeActiveFilter(this.handlerHeaderTableFilter);
            },
            deep: true
        },
        'handlerHeaderTableFilter.reference_order_status_id': {
            handler() {
                this.changeActiveFilter(this.handlerHeaderTableFilter);
            },
            deep: true
        },
        'handlerHeaderTableFilter.reference_sources_id': {
            handler() {
                this.changeActiveFilter(this.handlerHeaderTableFilter);
            },
            deep: true
        },
        'handlerHeaderTableFilter.teo_manager_id': {
            handler() {
                this.changeActiveFilter(this.handlerHeaderTableFilter, 'orderServiceLogisticTable');
            },
            deep: true
        },
        'handlerHeaderTableFilter.reference_property_type_id': {
            handler() {
                this.changeActiveFilter(this.handlerHeaderTableFilter, 'orderServiceLogisticTable');
            },
            deep: true
        },
        'handlerHeaderTableFilter.reference_property_status_id': {
            handler() {
                this.changeActiveFilter(this.handlerHeaderTableFilter, 'orderServiceLogisticTable');
            },
            deep: true
        },
        'items.current_page': {
            handler() {
                this.getDataHandler().then(() => {
                    this.$refs.table.$el.scrollTop = 0
                })
            },
            immediate: false
        },
        'tableSettings.perPage': function (){
            this.getDataHandler()
        },
        'itemsLocal.headers': {
            handler(headers) {
                if (headers) {
                    if (headers.length > 0) {
                        this.prepareForUploadingDefferedData();
                    }
                    this.visibleHeaders = headers.filter(field => field.visible)
                    this.visibleHeaders.unshift({
                        key: 'index',
                        label: '№',
                        class: 'table-small-td',
                    })
                    if (this.isNeedSelection && !this.$route.params.service_id) {
                        this.visibleHeaders.unshift({
                            key: 'selected',
                            label: '✓',
                            class: 'table-small-td',
                        })
                    }
                }
            },
            immediate: true,
            deep: true,
        },
        additionalParameter() {
            this.getDataHandler()
        },
        additionalHeaderTableParameter() {
            this.getDataHandler()
        },
        filter: debounce(function (value) {
            this.getDataHandler()
        }, 500)
    },
    mounted() {
        this.handlerHeightWindow()
        if (this.typeOfTableFilter == 'order') {
            let startSettings = this.$store.getters['userLocalSettings/FILTER_HEADER_SETTINGS'].orderTable;
            this.changeActiveFilter(startSettings);
        }
        if (this.typeOfTableFilter == 'order-service-logistic') {
            let startSettings = this.$store.getters['userLocalSettings/FILTER_HEADER_SETTINGS'].orderServiceLogisticTable;
            this.changeActiveFilter(startSettings, 'orderServiceLogisticTable');
        }

        if (!this.additionalParameter) {
            this.getDataHandler()
        }
    },
    methods: {
        ...mapActions('tablesSettings', ['setTableSettings']),

        processingSelectedFiltersForOutput(key, currentValue) {
            if (key == 'reference_order_status_id') {
                let referenceOrderStatusId = this.$store.getters.GROUPED_REFERENCE_PROPERTIES[35];
                for (let status in referenceOrderStatusId) {
                    if(currentValue == referenceOrderStatusId[status].id) {
                        return referenceOrderStatusId[status].name
                    }
                }
            }
            if (key == 'sales_manager_id' || key == 'teo_manager_id') {
                let userList = this.$store.getters.ALL_USER_LIST
                for (let userName in userList) {
                    if(currentValue == userList[userName].value) {
                        return userList[userName].text
                    }
                }
            }
            if (key == 'outsourcing_order') {
                if (currentValue == 'Да') {
                    return 'Только аутсорсинговые';
                }
                return 'Без аутсорсинговых';
            }
            if (key == 'reference_sources_id') {
                let referenceOrderSourceId = this.$store.getters.GROUPED_REFERENCE_PROPERTIES[4]
                for (let status in referenceOrderSourceId) {
                    if(currentValue == referenceOrderSourceId[status].id) {
                        return referenceOrderSourceId[status].name
                    }
                }
            }  

            if (key == 'reference_property_type_id') {
                let referencePropertyTypeId = this.$store.getters.GROUPED_REFERENCE_PROPERTIES[62]
                for (let status in referencePropertyTypeId) {
                    if(currentValue == referencePropertyTypeId[status].id) {
                        return referencePropertyTypeId[status].name
                    }
                }
            }  

            if (key == 'reference_property_status_id') {
                let referencePropertyStatusId = this.$store.getters.GROUPED_REFERENCE_PROPERTIES[28]
                for (let status in referencePropertyStatusId) {
                    if(currentValue == referencePropertyStatusId[status].id) {
                        return referencePropertyStatusId[status].name
                    }
                }
            }  
        },
        deleteSelectedFilterValues(index, sub_index = null) {
            if (sub_index === null) {
                for(let filterItem in this.handlerHeaderTableFilter) {
                    if(filterItem == index) {
                        this.handlerHeaderTableFilter[index] = Array.isArray(this.handlerHeaderTableFilter[index]) ? []:'';
                    }
                }
            } else {
                this.handlerHeaderTableFilter[index].splice(sub_index, 1);
            }
        },
        setOrderStatus(id) {
            const index = this.handlerHeaderTableFilter.reference_order_status_id.indexOf(id);

            if (index === -1) {
                this.handlerHeaderTableFilter.reference_order_status_id.push(id);
            }
        },
        setOrderSource(id) {
            const index = this.handlerHeaderTableFilter.reference_sources_id.indexOf(id);

            if (index === -1) {
                this.handlerHeaderTableFilter.reference_sources_id.push(id);
            }
        },
        setSalesManagerValue(id) {
            const index = this.handlerHeaderTableFilter.sales_manager_id.indexOf(id);

            if (index === -1) {
                this.handlerHeaderTableFilter.sales_manager_id.push(id);
            }
        },
        setTeoManagerValue(id) {
            const index = this.handlerHeaderTableFilter.teo_manager_id.indexOf(id);

            if (index === -1) {
                this.handlerHeaderTableFilter.teo_manager_id.push(id);
            }
        },
        setOrderServiceTypeOfTeo(id) {
            const index = this.handlerHeaderTableFilter.reference_property_type_id.indexOf(id);

            if (index === -1) {
                this.handlerHeaderTableFilter.reference_property_type_id.push(id);
            }
        },
        setOrderServiceStatus(id) {
            const index = this.handlerHeaderTableFilter.reference_property_status_id.indexOf(id);

            if (index === -1) {
                this.handlerHeaderTableFilter.reference_property_status_id.push(id);
            }
        },
        handlerHeightWindow() {
            let deleteHeight = 210
            this.computedWindowHeight = `${window.top.innerHeight - deleteHeight}px`
        },
        handlerTitleFilterPopover(column) {
            if (column == 'status') {
                return 'Статус'
            }
            if (column == 'salesManager') {
                return 'Sales менеджер'
            }
            if (column == 'outsourcing'){
                return 'Аутсорсинг'
            }
        },
        changeActiveFilter(value, filterTypeProp = 'orderTable') {
            let tableHeader = new String;

            this.$store.commit('userLocalSettings/SET_FILTER_HEADER_SETTINGS', {
                name: filterTypeProp,
                value: value
            });

            this.$set(this.handlerHeaderTableFilter, JSON.parse(JSON.stringify(value)));

            for (let key in value) {
                if (value[key]) {
                    if (Array.isArray(value[key])) {
                        // Не добавляем пустые массивы
                        if (value[key].length > 0) {
                            tableHeader += `&${key}=${value[key].join(',')}`;
                        }
                    } else {
                        // Не добавляем пустые строки
                        if (value[key] !== '') {
                            tableHeader += `&${key}=${value[key]}`;
                        }
                    }
                }
            }

            this.additionalHeaderTableParameter = tableHeader;
        },
        clearHeaderFilterParameter(key) {
            if (key == 'actions') {
                this.additionalHeaderTableParameter = ''
                this.handlerHeaderTableFilter = initalHeaderTableFilter()
                this.$store.commit('userLocalSettings/SET_FILTER_HEADER_SETTINGS', {
                    name: 'orderTable',
                    value: initalHeaderTableFilter()
                });
            }
        },
        getDataHandler: async function() {
            this.isTableBusy = true
            // this.sortParamsLine = ''
            let filter = '';
            if (this.filter) {
                filter = '&q=' + this.filter
            }
            if (!!this.specificApiRequest) {
                await api.call('get', this.api + `?page=${this.itemsLocal.current_page}&per_page=${this.tablePerPage}${this.additionalParameter}${this.additionalHeaderTableParameter}${this.additionalGetParameter}${this.sortParamsLine}${filter}&reference_order_status_id=${this.specificApiRequest.reference_order_status_id}`)
                .then((response) => {
                    this.isTableBusy = false
                    this.isTableRefreshing = false
                    this.itemsLocal = response.data
                    this.$emit('onLoad')
                })
            } else {
                await api.call('get', this.api + `?page=${this.itemsLocal.current_page}&per_page=${this.tablePerPage}${this.additionalParameter}${this.additionalHeaderTableParameter}${this.additionalGetParameter}${this.sortParamsLine}${filter}`)
                .then((response) => {
                    this.isTableBusy = false
                    this.isTableRefreshing = false
                    this.itemsLocal = response.data
                    this.$emit('onLoad')
                })
            }
        },
        sortingChanged(ctx) {
            let elasticSortBy = ''
            this.itemsLocal.headers.forEach((header, index) => {
                if (header.key == ctx.sortBy) {
                    elasticSortBy = header.sortBy
                }
            })
            let sortDirection = 'asc'
            if (ctx.sortDesc) {
                sortDirection = 'desc'
            }
            // this.sortParamsLine = `&sortBy=${ctx.sortBy}&sortDirection=${sortDirection}`
            this.sortParamsLine = `&elasticSortBy=${elasticSortBy}&sortBy=${ctx.sortBy}&sortDirection=${sortDirection}`
            this.getDataHandler()
        },
        createOrEditItemModal(index) {
            this.$emit('edit', index)
        },
        isClientQuotationPending(item) {
            return item && [359, 539, 540, 541, 542].includes(item.reference_status_id);
        },
        universalEmit(whatEmit, ...params) {
            this.$emit(whatEmit, ...params)
        },
        setFilteringApiParameter(additionalParameter) {
            // Сначала собираем параметры из заголовков таблицы
            const paramMap = new Map();
            const paramRegex = /&([^=&]+)=([^&]*)/g;
            let match;
            
            // Сначала добавляем параметры из заголовков таблицы (если они не пустые)
            if (this.additionalHeaderTableParameter) {
                while ((match = paramRegex.exec(this.additionalHeaderTableParameter)) !== null) {
                    const key = match[1];
                    const value = match[2];
                    if (value && value !== '') {
                        paramMap.set(key, value);
                    }
                }
            }
            
            // Затем перезаписываем параметрами из фильтра (они имеют приоритет)
            if (additionalParameter) {
                // Сбрасываем regex для нового поиска
                paramRegex.lastIndex = 0;
                while ((match = paramRegex.exec(additionalParameter)) !== null) {
                    const key = match[1];
                    const value = match[2];
                    // Перезаписываем значение, даже если оно пустое (чтобы сбросить фильтр)
                    if (value && value !== '') {
                        paramMap.set(key, value);
                    } else {
                        // Если значение пустое, удаляем параметр
                        paramMap.delete(key);
                    }
                }
            }
            
            // Формируем финальную строку параметров
            let finalParams = '';
            for (const [key, value] of paramMap.entries()) {
                finalParams += `&${key}=${value}`;
            }
            
            this.additionalParameter = finalParams;
            
            this.$emit('letMeKnowParameter', {
                name: 'additionalParameter',
                value: finalParams
            });
        },
        actionStickyMinWidth() {
            if (this.checkActionAllow('create_request')) {
                return 'min-width: 120px;'
            }
            return 'min-width: 70px;'
        },
        //@TODO: Накрутка логики, надо попробовать исправить
        companyNameTdOutput(item, value) {
            if (item.short_order && item.short_order == 1) {
                return item.temp_name
            } else if (!value && item.payer_company) {
                return item.payer_company.name;
            }

            return value
        },
        companiesNamesTdOutput(item, companies) {
            let listOfCompanies = []
            companies.forEach((company, index) => {
                listOfCompanies.push(company.name + ' (' + company.inn + ')')
            })

            return listOfCompanies.join(', ')
        },
        userNamesTdOutput(item, users) {
            if (users.length === 0) {
                return '—';
            }

            let listOfUsers = []
            users.forEach((user, index) => {
                listOfUsers.push(user.name)
            })

            return listOfUsers.join(', ')
        },
        rowClass(item, type) {
            if (!item || type !== 'row') return;

            // Приоритет: Оплата
            if (item.sum && item.sum_payment && item.sum !== 0 && item.sum <= (item.sum_payment + Math.abs(item.sum_difference))) {
                return 'table-success';
            }

            // Маппинг check_status
            const checkStatusMap = {
                fill: 'table-special-white',
                approved: 'table-pre-success',
                approved_by_company: 'table-pre-success',
                rejected: 'table-danger',
                revision: 'table-warning',
            };
            if (item.check_status && checkStatusMap[item.check_status]) {
                return checkStatusMap[item.check_status];
            }

            // Draft
            if (item.draft == 1) return 'table-special-white';

            // Маппинг reference_status_id
            const referenceStatusMap = {
                313: 'table-success',
                314: 'table-danger',
                362: 'table-danger',
                312: 'table-warning',
                424: 'table-warning',
                361: 'table-pre-success',
                421: 'table-success',
            };
            const refClass = referenceStatusMap[item.reference_status_id];
            if (refClass) {
                return refClass;
            }

            // Проверка 444
            if (item.checked_id == 444 || 
                (item.passport_checked_id == 444 && item.driver_card_checked_id == 444)) {
                return 'table-success';
            }

            // Фактическая доставка
            // if (item.converted_props && item.converted_props.fact_dostavka_gruza != null && item.converted_props.fact_dostavka_gruza != '') {
            //     return 'table-success';
            // }

            // Проверка 445
            if ([item.checked_id, item.passport_checked_id, item.driver_card_checked_id].includes(445)) {
                return 'table-danger';
            }

            return '';
        },
        refreshDataInTable() {
            this.isTableRefreshing = true
            this.getDataHandler()
        },
        // If we have columns with loading key - load data
        prepareForUploadingDefferedData() {
            let uploadElements = {
                'orders_list': [],
                'uploading_items': []
            }
            // take indexes of columns for uploading data
            this.itemsLocal.headers.forEach((item, index) => {
                if (item.loading && item.loading === true) {
                    uploadElements['uploading_items'].push({
                        'key': item.key,
                        'index': index,
                        'whatLoad': item.whatLoad,
                        'values': item.values,
                        'items': []
                    })
                }
            })

            // take id of elements for uploading
            this.itemsLocal.data.forEach((dataItem, dIndex) => {
                uploadElements['uploading_items'].forEach((uploadItem, uIndex) => {
                    dataItem[uploadItem.key] = ''
                    uploadItem.items.push({
                        index: dIndex,
                        id: dataItem.id,
                        value: []
                    })
                    uploadElements['orders_list'].push(dataItem.id)
                })
            })

            if (uploadElements['uploading_items'].length > 0) {
                this.uploadDefferedData(uploadElements)
            }
        },
        uploadDefferedData(uploadElements) {
            api.call('post', `${API_POST_SERVICE_PROPERTIES_BY_ORDER_LIST}`, {uploadElements: uploadElements}).then(({data}) => {
                data.uploading_items.forEach((item, index) => {
                    item.items.forEach((order, index) => {
                        if (order.value.length > 1) {
                            this.itemsLocal.data[order.index][item.key] = order.value.join(', ')
                        } else {
                            this.itemsLocal.data[order.index][item.key] = order.value[0]
                        }
                    })
                    this.itemsLocal.headers[item.index].loading = false
                })
            })
        },
        clickOnSelectAllRows() {
            if (this.selectAllRows) {
                this.onSelectAllRows()
            } else {
                this.onClearSelected()
            }
        },
        onRowSelected(items) {
            this.selected = items
        },
        onSelectAllRows() {
            this.$refs.table.selectAllRows()
        },
        onClearSelected() {
            this.$refs.table.clearSelected()
        },
        openScoreSidebar(data) {
            this.$emit('openScoreSidebar', data.item.id)
        },
        formatServicesList(services_list) {
            try {
                if (typeof services_list === 'string') {
                    services_list = JSON.parse(services_list);
                }

                if (services_list && services_list.data && Array.isArray(services_list.data) && services_list.data.length > 0) {
                    let finalText = '<ul class="service-list-in-main-table">';

                    services_list.data.forEach(service => {
                        finalText += `<li>${service.name}</li>`;
                    });

                    finalText += '</ul>';

                    return finalText;
                }
            } catch (e) {
                console.error("Ошибка при парсинге services_list в Table.vue:", e);
            }

            return '—';
        },
        getLatestComment(latest_status_with_comments) {
            try {
                if (latest_status_with_comments.user_comments && Array.isArray(latest_status_with_comments.user_comments) && latest_status_with_comments.user_comments.length > 0) {
                    let comment = `<div style="width: 250px;">${latest_status_with_comments.user_comments[0].comment}</div><div class="text-right"><small>${latest_status_with_comments.user_comments[0].created_at}</small></div>`;
                    return comment;
                }
            } catch (e) {
                console.error("Ошибка при парсинге latest_status_with_comments в Table.vue:", e);
            }

            return '—';            
        },
        findUserByServiceType(reference_service_id, manager_type, data) {
            if (data.item.service_users && data.item.service_users.length > 0) {
                let managerGlue = [];

                data.item.service_users.forEach(item => {
                    if (item.service.reference_service_id == reference_service_id) {
                        if (item.user) {
                            managerGlue.push(item.user.name);
                        }
                    }
                });

                if (managerGlue.length > 0) {
                    return managerGlue.join(', ');
                }
            }
            
            if (data.item.company && data.item.company[manager_type]) {
                return data.item.company[manager_type].name;
            } 

            return '—';
        }
    },
    destroyed() {
        this.itemsLocal.current_page = 1
    }
}
</script>
